import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'src/redux/hooks.ts';
import { ProfileInfo } from '../types';

/**
 * Custom hook for managing expense form state efficiently
 *
 * Optimizations:
 * - Memoized selectors to prevent unnecessary re-renders
 * - Reactive computed values that update when recordToEdit changes
 * - Stable callback references
 * - Optimized Redux selectors
 *
 * @returns Optimized state and handlers for expense form
 */
export const useExpenseFormState = () => {
  const location = useLocation();

  // Redux selectors - keeping original pattern for compatibility
  const permissions = useAppSelector((state) => state.user.permissions);
  const profile = useAppSelector((state) => state.user.profile);
  const expenseSettings = useAppSelector(
    (state) => state.client.selectedClientData.expense_settings
  );
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const clientId = useAppSelector(
    (state) => state.client.selectedClientData.id
  );

  // Local state for record to edit
  const [recordToEdit, setRecordToEdit] = useState<
    API.ExpenseHeaderCollection | undefined
  >(location.state?.expenseData as API.ExpenseHeaderCollection | undefined);

  const isExpenseOwnerChangeAvailable = !location.state?.myExpense;

  // Helper function to compute card state from recordToEdit
  const computeCardState = useCallback(
    (record?: API.ExpenseHeaderCollection) => {
      if (record) {
        return record.external ?? false;
      }
      // For new expenses, default to false (company card) if bank account exists
      // Default to true (private card) if no bank account configured
      return profile.bank_account === '';
    },
    [profile.bank_account]
  );

  // Helper function to compute expense ownership
  const computeExpenseOwnership = useCallback(
    (record?: API.ExpenseHeaderCollection) => {
      return location.state?.myExpense || record?.owner_id === profile.id;
    },
    [location.state?.myExpense, profile.id]
  );

  // Local state for form fields
  const [isPrivateCard, setIsPrivateCard] = useState<boolean>(
    computeCardState(recordToEdit)
  );
  const [isMyExpense, setIsMyExpense] = useState<boolean>(
    computeExpenseOwnership(recordToEdit)
  );
  const [submitterInfo, setSubmitterInfo] = useState<ProfileInfo>({
    firstName: '',
    lastName: '',
  });
  // Update local state when recordToEdit changes
  useEffect(() => {
    setIsPrivateCard(computeCardState(recordToEdit));
    setIsMyExpense(computeExpenseOwnership(recordToEdit));
  }, [recordToEdit, computeCardState, computeExpenseOwnership]);

  // Memoized event handlers
  const onPrivateCardChange = useCallback((checked: boolean) => {
    setIsPrivateCard(checked);
  }, []);

  const onOwnerSwitchChange = useCallback((checked: boolean) => {
    setIsMyExpense(checked);
  }, []);

  const updateRecordToEdit = useCallback(
    (record: API.ExpenseHeaderCollection) => {
      setRecordToEdit(record);
    },
    []
  );

  return {
    // Redux state
    permissions,
    profile,
    expenseSettings,
    accessToken,
    clientId,

    // Computed state
    recordToEdit,
    isExpenseOwnerChangeAvailable,

    // Local state
    submitterInfo,
    isPrivateCard,
    isMyExpense,
    setSubmitterInfo,

    // Event handlers
    onPrivateCardChange,
    onOwnerSwitchChange,
    updateRecordToEdit,
  };
};
